import React, { useEffect } from "react";
import { Link } from "react-router-dom";

const pageStyle: React.CSSProperties = {
  minHeight: "100vh",
  background: "#f6f7f9",
  fontFamily:
    '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  color: "#111827",
};

const navStyle: React.CSSProperties = {
  position: "sticky",
  top: 0,
  zIndex: 10,
  background: "rgba(255,255,255,0.98)",
  borderBottom: "1px solid #e5e7eb",
  backdropFilter: "blur(10px)",
  padding: "0 24px",
  height: 56,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const brandStyle: React.CSSProperties = {
  fontFamily: '"Gotham", system-ui, sans-serif',
  fontWeight: 700,
  fontSize: 16,
  color: "#0f172a",
  textDecoration: "none",
};

const contentStyle: React.CSSProperties = {
  maxWidth: 720,
  margin: "0 auto",
  padding: "48px 24px 80px",
};

const h1Style: React.CSSProperties = {
  fontFamily: '"Gotham", system-ui, sans-serif',
  fontSize: 28,
  fontWeight: 700,
  color: "#0f172a",
  marginBottom: 8,
};

const lastUpdatedStyle: React.CSSProperties = {
  fontSize: 13,
  color: "#6b7280",
  marginBottom: 40,
};

const h2Style: React.CSSProperties = {
  fontFamily: '"Gotham", system-ui, sans-serif',
  fontSize: 18,
  fontWeight: 600,
  color: "#0f172a",
  marginTop: 36,
  marginBottom: 12,
};

const pStyle: React.CSSProperties = {
  fontSize: 14,
  lineHeight: 1.75,
  color: "#374151",
  marginBottom: 16,
};

const ulStyle: React.CSSProperties = {
  fontSize: 14,
  lineHeight: 1.75,
  color: "#374151",
  marginBottom: 16,
  paddingLeft: 24,
};

const footerStyle: React.CSSProperties = {
  borderTop: "1px solid #e5e7eb",
  padding: "24px",
  textAlign: "center",
  fontSize: 13,
  color: "#6b7280",
};

const footerLinkStyle: React.CSSProperties = {
  color: "#0f172a",
  textDecoration: "none",
  fontWeight: 600,
  marginLeft: 8,
  marginRight: 8,
};

function PrivacyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div style={pageStyle}>
      {/* Navbar */}
      <nav style={navStyle}>
        <Link to="/" style={brandStyle}>
          Napkin
        </Link>
        <div style={{ display: "flex", gap: 16 }}>
          <Link
            to="/terms"
            style={{ fontSize: 13, color: "#6b7280", textDecoration: "none" }}
          >
            Terms of Service
          </Link>
          <Link
            to="/login"
            style={{ fontSize: 13, color: "#0f172a", textDecoration: "none", fontWeight: 600 }}
          >
            Log in
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main style={contentStyle}>
        <h1 style={h1Style}>Privacy Policy</h1>
        <p style={lastUpdatedStyle}>Last updated: February 2026</p>

        <p style={pStyle}>
          At Napkin, we take your privacy seriously. This Privacy Policy explains how we collect, use,
          store, and protect your personal information when you use our electronic signature and
          document management platform (the "Service"). By using the Service, you consent to the
          practices described in this policy.
        </p>

        <h2 style={h2Style}>1. Information We Collect</h2>
        <p style={pStyle}>We collect the following categories of information:</p>
        <ul style={ulStyle}>
          <li>
            <strong>Account Information:</strong> Name, email address, password (hashed), and
            authentication method (email/password or Google OAuth).
          </li>
          <li>
            <strong>Documents and Signatures:</strong> Documents you upload, create, or sign through
            the Service, including electronic signature images, timestamps, and signer
            identification data.
          </li>
          <li>
            <strong>Usage Data:</strong> Information about how you interact with the Service,
            including pages visited, features used, device type, browser type, IP address, and
            session duration.
          </li>
          <li>
            <strong>Payment Information:</strong> If you subscribe to a paid plan, payment details
            are collected and processed by our payment processor (Stripe). We do not store your full
            credit card number on our servers.
          </li>
          <li>
            <strong>Communications:</strong> Emails and messages you send to us, including support
            requests.
          </li>
        </ul>

        <h2 style={h2Style}>2. How We Use Information</h2>
        <p style={pStyle}>We use the information we collect to:</p>
        <ul style={ulStyle}>
          <li>Provide, maintain, and improve the Service.</li>
          <li>Process electronic signatures and maintain audit trails for document integrity.</li>
          <li>Authenticate your identity and secure your account.</li>
          <li>Send transactional emails (signature requests, reminders, confirmations).</li>
          <li>Process payments and manage subscriptions.</li>
          <li>Respond to your inquiries and provide customer support.</li>
          <li>Analyze usage patterns to improve performance and user experience.</li>
          <li>Comply with legal obligations and enforce our Terms of Service.</li>
        </ul>

        <h2 style={h2Style}>3. Document Storage and Security</h2>
        <p style={pStyle}>
          Documents and signatures are stored securely using industry-standard encryption. We employ
          encryption in transit (TLS/SSL) and at rest to protect your data. Access to document data
          is restricted to authorized personnel and the document owner and designated recipients.
        </p>
        <p style={pStyle}>
          We maintain audit trails for all signature events, including timestamps, IP addresses, and
          signer identification, to ensure the integrity and legal validity of signed documents.
        </p>

        <h2 style={h2Style}>4. Third-Party Services</h2>
        <p style={pStyle}>
          We use the following third-party services to operate the platform. Each has its own privacy
          policy governing the use of your data:
        </p>
        <ul style={ulStyle}>
          <li>
            <strong>Stripe:</strong> Payment processing for subscriptions. Stripe collects and
            processes payment information in accordance with their privacy policy.
          </li>
          <li>
            <strong>Google OAuth:</strong> Optional authentication method. When you sign in with
            Google, we receive your name and email address from Google.
          </li>
          <li>
            <strong>Email Providers:</strong> We use third-party email services to send transactional
            emails such as signature requests, reminders, and account notifications.
          </li>
          <li>
            <strong>Cloud Hosting:</strong> Our infrastructure is hosted on secure cloud platforms
            that comply with industry-standard security certifications.
          </li>
        </ul>

        <h2 style={h2Style}>5. Data Retention</h2>
        <p style={pStyle}>
          We retain your account information and documents for as long as your account is active or as
          needed to provide the Service. Signed documents and their audit trails are retained to
          maintain the legal validity of signatures. If you delete your account, we will delete your
          personal data within 30 days, except where retention is required by law or to maintain
          existing signature records.
        </p>

        <h2 style={h2Style}>6. Your Rights</h2>
        <p style={pStyle}>
          Depending on your jurisdiction, you may have the following rights regarding your personal
          data:
        </p>
        <ul style={ulStyle}>
          <li>
            <strong>Access:</strong> Request a copy of the personal data we hold about you.
          </li>
          <li>
            <strong>Correction:</strong> Request correction of inaccurate or incomplete data.
          </li>
          <li>
            <strong>Deletion:</strong> Request deletion of your personal data, subject to legal
            retention requirements.
          </li>
          <li>
            <strong>Export:</strong> Request a portable copy of your data in a commonly used format.
          </li>
          <li>
            <strong>Restriction:</strong> Request restriction of processing in certain circumstances.
          </li>
          <li>
            <strong>Objection:</strong> Object to processing of your data for certain purposes.
          </li>
        </ul>
        <p style={pStyle}>
          To exercise any of these rights, please contact us at privacy@napkinsend.com. We will
          respond to your request within 30 days.
        </p>

        <h2 style={h2Style}>7. Cookies</h2>
        <p style={pStyle}>
          We use cookies and similar technologies to maintain your session, remember your
          preferences, and analyze usage patterns. Essential cookies are required for the Service to
          function properly. You can control cookie preferences through your browser settings,
          though disabling essential cookies may affect the functionality of the Service.
        </p>

        <h2 style={h2Style}>8. International Data Transfers</h2>
        <p style={pStyle}>
          If you are located outside the United States, please be aware that your data may be
          transferred to, stored, and processed in the United States. By using the Service, you
          consent to the transfer of your data to the United States. We take appropriate measures
          to ensure that your data is treated securely and in accordance with this Privacy Policy
          and applicable data protection laws, including the General Data Protection Regulation
          (GDPR) where applicable.
        </p>

        <h2 style={h2Style}>9. Children's Privacy</h2>
        <p style={pStyle}>
          The Service is not intended for use by individuals under the age of 18. We do not knowingly
          collect personal information from children. If we become aware that we have collected data
          from a child, we will take steps to delete it promptly.
        </p>

        <h2 style={h2Style}>10. Changes to This Privacy Policy</h2>
        <p style={pStyle}>
          We may update this Privacy Policy from time to time. When we make material changes, we will
          notify you by email or by posting a prominent notice on the Service. Your continued use of
          the Service after such changes constitutes your acceptance of the updated policy.
        </p>

        <h2 style={h2Style}>11. Contact Information</h2>
        <p style={pStyle}>
          If you have any questions or concerns about this Privacy Policy or our data practices,
          please contact us at:
        </p>
        <p style={pStyle}>
          <strong>Napkin</strong>
          <br />
          Email: privacy@napkinsend.com
          <br />
          Website: napkinsend.com
        </p>
      </main>

      {/* Footer */}
      <footer style={footerStyle}>
        <span>&copy; {new Date().getFullYear()} Napkin. All rights reserved.</span>
        <br style={{ marginBottom: 8 }} />
        <Link to="/" style={footerLinkStyle}>
          Home
        </Link>
        <Link to="/terms" style={footerLinkStyle}>
          Terms of Service
        </Link>
      </footer>
    </div>
  );
}

export default PrivacyPage;
